import { googleMapsUrl, liveListFor, markerOf, staticMatch } from "../shared/liveList";
import type { PartnerMapContent, Place } from "./types";

/**
 * A partner map's content with its synced list laid over it: the list's places become the highlights and its id the
 * "Save this map" link (`saveMapUrl`). Everything else (the base, the venues, the week program, the theme, the header)
 * stays the content file's. See `shared/liveList.ts` for what the list decides and what the file keeps.
 *
 * Without a list for this slug it returns `content` itself, so the page renders exactly what it rendered before the
 * loader existed. A list with no places keeps the file's highlights but still takes the list's id.
 */
export function withLiveList(content: PartnerMapContent, data: unknown): PartnerMapContent {
	const live = liveListFor(data, content.slug);
	if (!live) return content;
	if (!live.items.length) return { ...content, cta: { ...content.cta, listId: live.listId } };

	// A slug names one place across highlights and venues (`indexPlaces`): a list place never takes a venue's, nor the
	// keys the page uses for its base.
	const taken = new Set(["base", "hostel", ...content.venues.map((venue) => venue.slug)]);
	const uniqueSlug = (wanted: string): string => {
		let slug = wanted;
		for (let n = 2; taken.has(slug); n++) slug = `${wanted}-${n}`;
		taken.add(slug);
		return slug;
	};

	const highlights = live.items.map((item, index): Place => {
		const known = staticMatch(item, content.highlights);
		const { grp, icon } = known ?? markerOf(item);
		const photo = known?.photo ?? item.photo ?? undefined;
		const credit = known?.photo ? known.credit : item.photo ? item.credit : null;
		return {
			slug: uniqueSlug(known?.slug ?? item.slug ?? `place-${index + 1}`),
			name: known?.name ?? item.name,
			grp,
			icon,
			...(photo ? { photo } : {}),
			...(credit ? { credit } : {}),
			lat: item.lat,
			lng: item.lng,
			googleUrl: known?.googleUrl ?? googleMapsUrl(item),
			...((known?.label ?? item.label) ? { label: known?.label ?? item.label ?? undefined } : {}),
			...(item.line || known?.blurb ? { blurb: item.line || known?.blurb } : {}),
			// what the content file sells or suggests for a place is not the list's to know
			...(known?.tickets ? { tickets: known.tickets } : {}),
			...(known?.after ? { after: known.after } : {}),
		};
	});
	return { ...content, highlights, cta: { ...content.cta, listId: live.listId } };
}
